import { useState, useMemo, useCallback, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import "./ReportPage.css";

const publicClient = axios.create({ baseURL: "" });

const ONSET_OPTIONS = [
    "Immediately (within minutes)",
    "Within 1 hour",
    "1-6 hours",
    "6-24 hours",
    "More than 24 hours",
];
  
const DURATION_OPTIONS = [
    "Less than 1 hour",
    "1-6 hours",
    "6-24 hours",
    "More than 24 hours",
    "Still ongoing",
];

// ---------------------------------------------------------------------------
// ReportPage — full-page multi-step questionnaire (mobile-first)
// Route: /compliance/:companySlug/report/:coaId
// ---------------------------------------------------------------------------
export default function ReportPage() {
    const { companySlug, coaId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const routeState = location.state;

    // ── Data loading (fast path via route state, fallback via API) ──

    const [coa, setCoa] = useState(routeState?.coa || null);
    const [company, setCompany] = useState(routeState?.company || "");
    const [licenseNo, setLicenseNo] = useState(routeState?.licenseNo || "");
    const [pageLoading, setPageLoading] = useState(!routeState?.coa);
    const [pageError, setPageError] = useState(null);

    useEffect(() => {
        if (routeState?.coa) return;
        publicClient.get(`/public/compliance/${companySlug}`)
            .then((res) => {
                const match = res.data.coas.find((c) => c.coa_id === Number(coaId));
                if (!match) { setPageError("Product not found."); return; }
                setCoa(match);
                setCompany(res.data.license.company);
                setLicenseNo(res.data.license.licenseNo);
            })
            .catch(() => setPageError("Failed to load product data."))
            .finally(() => setPageLoading(false));
    }, [companySlug, coaId, routeState]);

    useEffect(() => {
        document.title = "Report a Product Concern — DYC";
        return () => { document.title = "DYC Compliance"; };
    }, []);

    const goBack = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    // ── Survey state ──

    const [stepIndex, setStepIndex] = useState(0);
    const [direction, setDirection] = useState("forward");
    const [animKey, setAnimKey] = useState(0);
    const [reportId, setReportId] = useState(null);
    const [refNumber, setRefNumber] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [errors, setErrors] = useState({});
    const [draftLoading, setDraftLoading] = useState(true);
    const [hasDraft, setHasDraft] = useState(false);
    const [draftStep, setDraftStep] = useState(0);

    const [form, setForm] = useState({
        contact_name: "",
        contact_phone: "",
        contact_email: "",
        purchase_location: "",
        purchase_date: "",
        is_adverse_effect: false,
        is_quality_problem: false,
        is_other: false,
        other_description: "",
        symptoms: "",
        symptom_onset: "",
        symptom_duration: "",
        sought_medical_attention: null,
        consumption_datetime: "",
        consumption_amount: "",
        consumed_with: "",
        unusual_observations: "",
        problem_detail: "",
        has_product_remaining: null,
        additional_details: "",
        contact_preference: "no",
        notification_preference: "no",
        confirmed_accurate: false,
        _hp: "",
    });

    const set = useCallback((field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        setErrors((prev) => {
            if (!prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
    }, []);

    // ── Active steps (conditional based on classification) ──

    const activeSteps = useMemo(() => {
        const steps = ["intro", "contact", "product", "classification"];
        if (form.is_adverse_effect) steps.push("health");
        if (form.is_quality_problem) steps.push("observations");
        steps.push("additional", "notification");
        return steps;
    }, [form.is_adverse_effect, form.is_quality_problem]);

    const currentStep = activeSteps[stepIndex];
    const isFirst = stepIndex === 0;
    const isLast = stepIndex === activeSteps.length - 1;

    // ── Load saved draft from localStorage on mount ──

    const DRAFT_KEY = useMemo(() => `dyc-report-draft-${coaId}`, [coaId]);

    useEffect(() => {
        const loadDraft = async () => {
            try {
                const stored = localStorage.getItem(DRAFT_KEY);
                if (!stored) { setDraftLoading(false); return; }

                const { report_id: savedId } = JSON.parse(stored);
                if (!savedId) { setDraftLoading(false); return; }

                const res = await publicClient.get(`/public/compliance/report/${savedId}`);
                const d = res.data;

                setReportId(d.id);
                setRefNumber(d.reference_number);
                setForm((prev) => ({
                    ...prev,
                    contact_name: d.contact_name || "",
                    contact_phone: d.contact_phone || "",
                    contact_email: d.contact_email || "",
                    purchase_location: d.purchase_location || "",
                    purchase_date: d.purchase_date ? d.purchase_date.slice(0, 10) : "",
                    is_adverse_effect: !!d.is_adverse_effect,
                    is_quality_problem: !!d.is_quality_problem,
                    is_other: !!d.is_other,
                    other_description: d.other_description || "",
                    symptoms: d.symptoms || "",
                    symptom_onset: d.symptom_onset || "",
                    symptom_duration: d.symptom_duration || "",
                    sought_medical_attention: d.sought_medical_attention == null ? null : !!d.sought_medical_attention,
                    consumption_datetime: d.consumption_datetime || "",
                    consumption_amount: d.consumption_amount || "",
                    consumed_with: d.consumed_with || "",
                    unusual_observations: d.unusual_observations || "",
                    problem_detail: d.problem_detail || "",
                    has_product_remaining: d.has_product_remaining == null ? null : !!d.has_product_remaining,
                    additional_details: d.additional_details || "",
                    contact_preference: d.contact_preference || "no",
                    notification_preference: d.notification_preference || "no",
                    confirmed_accurate: !!d.confirmed_accurate,
                }));
                setDraftStep(d.current_step || 0);
                setHasDraft(true);
            } catch {
                localStorage.removeItem(DRAFT_KEY);
            } finally {
                setDraftLoading(false);
            }
        };
        loadDraft();
    }, [DRAFT_KEY, coaId]);

    // ── Validation per step ──

    const validateStep = (step) => {
        const errs = {};

        if (step === "contact") {
            if (form.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email)) {
                errs.contact_email = "Please enter a valid email address.";
            }
        }

        if (step === "classification") {
            if (!form.is_adverse_effect && !form.is_quality_problem && !form.is_other) {
                errs.classification = "Please select at least one issue type.";
            }
            if (form.is_other && !form.other_description.trim()) {
                errs.other_description = "Please describe the issue.";
            }
        }

        if (step === "health") {
            if (!form.symptoms.trim()) errs.symptoms = "Please describe your symptoms.";
            if (!form.symptom_onset) errs.symptom_onset = "Required.";
            if (!form.symptom_duration) errs.symptom_duration = "Required.";
            if (form.sought_medical_attention === null) errs.sought_medical_attention = "Required.";
            if (!form.consumption_datetime) errs.consumption_datetime = "Required.";
            if (!form.consumption_amount.trim()) errs.consumption_amount = "Required.";
        }

        if (step === "observations") {
            if (!form.unusual_observations.trim() && !form.problem_detail.trim()) {
                errs.unusual_observations = "Please describe what you observed.";
            }
            if (form.has_product_remaining === null) errs.has_product_remaining = "Required.";
        }

        if (step === "notification") {
            if (!form.confirmed_accurate) {
                errs.confirmed_accurate = "You must confirm the report is accurate.";
            }
        }

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    // ── Auto-save to backend ──

    const saveProgress = async (isSubmit = false) => {
        if (!coa || form._hp) return true;

        setSaving(true);
        setSaveError(null);
        try {
            const payload = { ...form, report_id: reportId, coa_id: coa.coa_id,
                license_no: licenseNo, product_name: coa.product_name,
                dyc_id: coa.dycId || "", company,
                current_step: stepIndex, submit: isSubmit };
            delete payload._hp;

            const res = await publicClient.post("/public/compliance/report", payload);
            const newId = res.data.report_id;
            if (!reportId) setReportId(newId);
            setRefNumber(res.data.reference_number);

            if (isSubmit) {
                localStorage.removeItem(DRAFT_KEY);
            } else {
                localStorage.setItem(DRAFT_KEY, JSON.stringify({ report_id: newId || reportId }));
            }
            return true;
        } catch (err) {
            console.error("Report save error:", err);
            if (isSubmit) {
                setSaveError("Failed to submit. Please try again.");
                return false;
            }
            return true;
        } finally {
            setSaving(false);
        }
    };

    // ── Navigation ──

    const handleResume = () => {
        const targetStep = Math.min(draftStep, activeSteps.length - 1);
        setDirection("forward");
        setAnimKey((k) => k + 1);
        setStepIndex(targetStep > 0 ? targetStep : 1);
    };

    const handleNext = async () => {
        if (currentStep !== "intro" && !validateStep(currentStep)) return;
        if (currentStep !== "intro") await saveProgress(false);

        setDirection("forward");
        setAnimKey((k) => k + 1);
        setStepIndex((i) => Math.min(i + 1, activeSteps.length - 1));
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleBack = () => {
        setErrors({});
        setDirection("backward");
        setAnimKey((k) => k + 1);
        setStepIndex((i) => Math.max(0, i - 1));
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleSubmit = async () => {
        if (!validateStep("notification")) return;
        const ok = await saveProgress(true);
        if (ok) {
            setSubmitted(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

    // ── Slide renderers ──

    const PHYSICIAN_URL = "https://app.acuityscheduling.com/schedule/1c6e88df/appointment/96821840/calendar/5826382?appointmentTypeIds[]=96821840";

    const renderIntro = () => (
        <>
            <h2 className="rpt-slide__title">Report a Product Concern</h2>
            <div className="rpt-emergency-banner">
                If you are having a medical emergency, dial 911.
            </div>
            <div className="rpt-intro__time">Estimated time: 3-5 minutes</div>
            <p className="rpt-slide__subtitle">
                Your feedback helps identify potential safety issues and protect other consumers.
            </p>
            {coa && (
                <div className="rpt-intro__product">
                    <div className="rpt-intro__product-row">
                        <span className="rpt-intro__product-label">Product</span>
                        <span className="rpt-intro__product-value">{coa.product_name}</span>
                    </div>
                    {coa.dycId && (
                        <div className="rpt-intro__product-row">
                            <span className="rpt-intro__product-label">DYC ID</span>
                            <span className="rpt-intro__product-value">{coa.dycId}</span>
                        </div>
                    )}
                    <div className="rpt-intro__product-row">
                        <span className="rpt-intro__product-label">Company</span>
                        <span className="rpt-intro__product-value">{company}</span>
                    </div>
                </div>
            )}
            {hasDraft ? (
                <div className="rpt-intro__draft-banner">
                    <p className="rpt-intro__draft-text">
                        You have a saved draft for this product. Pick up where you left off,
                        or start over with a new report.
                    </p>
                    <div className="rpt-intro__draft-actions">
                        <button className="rpt-nav__next" onClick={handleResume}>
                            Continue Report
                        </button>
                        <button className="rpt-nav__back" onClick={() => {
                            localStorage.removeItem(DRAFT_KEY);
                            setHasDraft(false);
                            setReportId(null);
                            setRefNumber(null);
                            setDraftStep(0);
                            setForm((prev) => ({
                                ...prev,
                                contact_name: "", contact_phone: "", contact_email: "",
                                purchase_location: "", purchase_date: "",
                                is_adverse_effect: false, is_quality_problem: false, is_other: false,
                                other_description: "",
                                symptoms: "", symptom_onset: "", symptom_duration: "",
                                sought_medical_attention: null, consumption_datetime: "",
                                consumption_amount: "", consumed_with: "",
                                unusual_observations: "", problem_detail: "", has_product_remaining: null,
                                additional_details: "",
                                contact_preference: "no", notification_preference: "no",
                                confirmed_accurate: false,
                            }));
                        }}>
                            Start Over
                        </button>
                    </div>
                </div>
            ) : (
                <p className="rpt-intro__note">
                    Your responses are saved automatically as you progress through each page.
                    All fields marked (optional) may be skipped.
                </p>
            )}
        </>
    );

    const renderContact = () => (
        <>
            <h2 className="rpt-slide__title">Contact Information</h2>
            <p className="rpt-slide__subtitle">
                Optional, but helpful for follow-up. Your information is kept confidential.
            </p>
            <div className="rpt-field">
                <label className="rpt-label rpt-label--optional">Full Name</label>
                <input className="rpt-input" type="text" value={form.contact_name}
                    onChange={(e) => set("contact_name", e.target.value)} maxLength={255}
                    placeholder="Your full name" autoComplete="name" />
            </div>
            <div className="rpt-field">
                <label className="rpt-label rpt-label--optional">Phone Number</label>
                <input className="rpt-input" type="tel" value={form.contact_phone}
                    onChange={(e) => set("contact_phone", e.target.value)} maxLength={30}
                    placeholder="(555) 555-5555" autoComplete="tel" />
            </div>
            <div className="rpt-field">
                <label className="rpt-label rpt-label--optional">Email Address</label>
                <input className={`rpt-input ${errors.contact_email ? "rpt-input--error" : ""}`}
                    type="email" value={form.contact_email}
                    onChange={(e) => set("contact_email", e.target.value)} maxLength={255}
                    placeholder="you@example.com" autoComplete="email" />
                {errors.contact_email && <span className="rpt-error">{errors.contact_email}</span>}
            </div>
        </>
    );

    const renderProduct = () => (
        <>
            <h2 className="rpt-slide__title">Product Information</h2>
            <p className="rpt-slide__subtitle">
                The product details below are pre-filled. Please add where and when you purchased it.
            </p>
            {coa && (
                <>
                    <div className="rpt-field">
                        <label className="rpt-label">Product Name</label>
                        <div className="rpt-readonly">{coa.product_name}</div>
                    </div>
                    <div className="rpt-field-row">
                        {coa.dycId && (
                            <div className="rpt-field">
                                <label className="rpt-label">DYC ID</label>
                                <div className="rpt-readonly">{coa.dycId}</div>
                            </div>
                        )}
                        <div className="rpt-field">
                            <label className="rpt-label">Company</label>
                            <div className="rpt-readonly">{company}</div>
                        </div>
                    </div>
                </>
            )}
            <hr className="rpt-divider" />
            <div className="rpt-field">
                <label className="rpt-label rpt-label--optional">Where did you purchase this product?</label>
                <input className="rpt-input" type="text" value={form.purchase_location}
                    onChange={(e) => set("purchase_location", e.target.value)} maxLength={500}
                    placeholder="Store name or website" />
            </div>
            <div className="rpt-field">
                <label className="rpt-label rpt-label--optional">Date of purchase</label>
                <input className="rpt-input" type="date" value={form.purchase_date}
                    onChange={(e) => set("purchase_date", e.target.value)} />
            </div>
        </>
    );

    const renderClassification = () => (
        <>
            <h2 className="rpt-slide__title">What issue did you experience?</h2>
            <p className="rpt-slide__subtitle">Check all that apply.</p>
            {errors.classification && <div className="rpt-save-error">{errors.classification}</div>}
            <div className="rpt-checkbox-group">
                <label className={`rpt-checkbox ${form.is_adverse_effect ? "rpt-checkbox--checked" : ""}`}>
                    <input type="checkbox" checked={form.is_adverse_effect}
                        onChange={(e) => set("is_adverse_effect", e.target.checked)} />
                    <span className="rpt-checkbox__text">Adverse health effect / illness</span>
                </label>
                <label className={`rpt-checkbox ${form.is_quality_problem ? "rpt-checkbox--checked" : ""}`}>
                    <input type="checkbox" checked={form.is_quality_problem}
                        onChange={(e) => set("is_quality_problem", e.target.checked)} />
                    <span className="rpt-checkbox__text">
                        Product quality problem (e.g., off taste, smell, appearance, foreign object)
                    </span>
                </label>
                <label className={`rpt-checkbox ${form.is_other ? "rpt-checkbox--checked" : ""}`}>
                    <input type="checkbox" checked={form.is_other}
                        onChange={(e) => set("is_other", e.target.checked)} />
                    <span className="rpt-checkbox__text">Other</span>
                </label>
            </div>
            {form.is_other && (
                <div className="rpt-field" style={{ marginTop: 14 }}>
                    <label className="rpt-label">Please describe</label>
                    <textarea className={`rpt-textarea ${errors.other_description ? "rpt-textarea--error" : ""}`}
                        value={form.other_description}
                        onChange={(e) => set("other_description", e.target.value)} maxLength={2000}
                        placeholder="Describe the issue you experienced..." rows={3} />
                    {errors.other_description && <span className="rpt-error">{errors.other_description}</span>}
                </div>
            )}
        </>
    );

    const renderHealth = () => (
        <>
            <h2 className="rpt-slide__title">Health Effects</h2>
            <p className="rpt-slide__subtitle">
                Please describe the health effects you experienced after using this product.
            </p>
            <div className="rpt-field">
                <label className="rpt-label">What symptoms did you experience?</label>
                <textarea className={`rpt-textarea ${errors.symptoms ? "rpt-textarea--error" : ""}`}
                    value={form.symptoms}
                    onChange={(e) => set("symptoms", e.target.value)} maxLength={2000}
                    placeholder="e.g., nausea, vomiting, rash, dizziness, paranoia, anxiety, headache, respiratory irritation..."
                    rows={3} />
                {errors.symptoms && <span className="rpt-error">{errors.symptoms}</span>}
            </div>
            <div className="rpt-field-row">
                <div className="rpt-field">
                    <label className="rpt-label">How soon did symptoms start?</label>
                    <select className={`rpt-select ${errors.symptom_onset ? "rpt-input--error" : ""}`}
                        value={form.symptom_onset}
                        onChange={(e) => set("symptom_onset", e.target.value)}>
                        <option value="">Select...</option>
                        {ONSET_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                    {errors.symptom_onset && <span className="rpt-error">{errors.symptom_onset}</span>}
                </div>
                <div className="rpt-field">
                    <label className="rpt-label">How long did symptoms last?</label>
                    <select className={`rpt-select ${errors.symptom_duration ? "rpt-input--error" : ""}`}
                        value={form.symptom_duration}
                        onChange={(e) => set("symptom_duration", e.target.value)}>
                        <option value="">Select...</option>
                        {DURATION_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                    {errors.symptom_duration && <span className="rpt-error">{errors.symptom_duration}</span>}
                </div>
            </div>
            <div className="rpt-field">
                <label className="rpt-label">Did you seek medical attention?</label>
                <div className="rpt-yesno">
                    <button type="button"
                        className={`rpt-yesno__btn ${form.sought_medical_attention === true ? "rpt-yesno__btn--active" : ""}`}
                        onClick={() => set("sought_medical_attention", true)}>Yes</button>
                    <button type="button"
                        className={`rpt-yesno__btn ${form.sought_medical_attention === false ? "rpt-yesno__btn--active" : ""}`}
                        onClick={() => set("sought_medical_attention", false)}>No</button>
                </div>
                {errors.sought_medical_attention && <span className="rpt-error">{errors.sought_medical_attention}</span>}
            </div>
            <div className="rpt-field">
                <label className="rpt-label">Date and time you consumed/used the product</label>
                <input className={`rpt-input ${errors.consumption_datetime ? "rpt-input--error" : ""}`}
                    type="datetime-local" value={form.consumption_datetime}
                    onChange={(e) => set("consumption_datetime", e.target.value)} />
                {errors.consumption_datetime && <span className="rpt-error">{errors.consumption_datetime}</span>}
            </div>
            <div className="rpt-field">
                <label className="rpt-label">How much of the product did you consume?</label>
                <input className={`rpt-input ${errors.consumption_amount ? "rpt-input--error" : ""}`}
                    type="text" value={form.consumption_amount}
                    onChange={(e) => set("consumption_amount", e.target.value)} maxLength={500}
                    placeholder='e.g., "2 puffs", "half a gummy", "1mL tincture"' />
                {errors.consumption_amount && <span className="rpt-error">{errors.consumption_amount}</span>}
            </div>
            <div className="rpt-field">
                <label className="rpt-label rpt-label--optional">
                    Did you consume it alone or with other foods/drinks?
                </label>
                <textarea className="rpt-textarea" value={form.consumed_with}
                    onChange={(e) => set("consumed_with", e.target.value)} maxLength={2000}
                    placeholder="List anything else you consumed within a few hours of using this product."
                    rows={2} />
            </div>
        </>
    );

    const renderObservations = () => (
        <>
            <h2 className="rpt-slide__title">Product Observations</h2>
            <p className="rpt-slide__subtitle">
                Describe anything unusual you noticed about the product.
            </p>
            <div className="rpt-field">
                <label className="rpt-label">
                    Did you notice anything unusual about the product before or after opening?
                </label>
                <textarea className={`rpt-textarea ${errors.unusual_observations ? "rpt-textarea--error" : ""}`}
                    value={form.unusual_observations}
                    onChange={(e) => set("unusual_observations", e.target.value)} maxLength={2000}
                    placeholder="e.g., strange smell, color, texture, taste, packaging damage, leaks, foreign objects..."
                    rows={3} />
                {errors.unusual_observations && <span className="rpt-error">{errors.unusual_observations}</span>}
            </div>
            <div className="rpt-field">
                <label className="rpt-label rpt-label--optional">
                    Describe any other aspects of the product problem in detail
                </label>
                <textarea className="rpt-textarea" value={form.problem_detail}
                    onChange={(e) => set("problem_detail", e.target.value)} maxLength={2000}
                    placeholder="Any additional details about what was wrong with the product..."
                    rows={3} />
            </div>
            <div className="rpt-field">
                <label className="rpt-label">Do you still have any of the product, packaging, or photos?</label>
                <div className="rpt-yesno">
                    <button type="button"
                        className={`rpt-yesno__btn ${form.has_product_remaining === true ? "rpt-yesno__btn--active" : ""}`}
                        onClick={() => set("has_product_remaining", true)}>Yes</button>
                    <button type="button"
                        className={`rpt-yesno__btn ${form.has_product_remaining === false ? "rpt-yesno__btn--active" : ""}`}
                        onClick={() => set("has_product_remaining", false)}>No</button>
                </div>
                {errors.has_product_remaining && <span className="rpt-error">{errors.has_product_remaining}</span>}
            </div>
        </>
    );

    const renderAdditional = () => (
        <>
            <h2 className="rpt-slide__title">Additional Information</h2>
            <p className="rpt-slide__subtitle">
                Any other relevant details that may help with the investigation.
            </p>
            <div className="rpt-field">
                <label className="rpt-label rpt-label--optional">
                    Additional details (e.g., storage conditions, how the product was handled)
                </label>
                <textarea className="rpt-textarea" value={form.additional_details}
                    onChange={(e) => set("additional_details", e.target.value)} maxLength={2000}
                    placeholder="Include any other information you think might be relevant..."
                    rows={5} />
            </div>
        </>
    );

    const renderNotification = () => (
        <>
            <h2 className="rpt-slide__title">Review & Submit</h2>
            <p className="rpt-slide__subtitle">
                Let us know how you'd like to be contacted, then submit your report.
            </p>
            <div className="rpt-field">
                <label className="rpt-label">Can we contact you to gather more information?</label>
                <div className="rpt-radio-group">
                    {[
                        { value: "no", label: "No" },
                        { value: "email", label: "Yes, via email" },
                        { value: "text", label: "Yes, via text" },
                    ].map((opt) => (
                        <label key={opt.value}
                            className={`rpt-radio ${form.contact_preference === opt.value ? "rpt-radio--selected" : ""}`}>
                            <input type="radio" name="contact_preference" value={opt.value}
                                checked={form.contact_preference === opt.value}
                                onChange={() => set("contact_preference", opt.value)} />
                            <span className="rpt-radio__text">{opt.label}</span>
                        </label>
                    ))}
                </div>
            </div>
            <div className="rpt-field">
                <label className="rpt-label">Would you like to be notified of the resolution?</label>
                <div className="rpt-radio-group">
                    {[
                        { value: "no", label: "No" },
                        { value: "email", label: "Yes, via email" },
                        { value: "text", label: "Yes, via text" },
                    ].map((opt) => (
                        <label key={opt.value}
                            className={`rpt-radio ${form.notification_preference === opt.value ? "rpt-radio--selected" : ""}`}>
                            <input type="radio" name="notification_preference" value={opt.value}
                                checked={form.notification_preference === opt.value}
                                onChange={() => set("notification_preference", opt.value)} />
                            <span className="rpt-radio__text">{opt.label}</span>
                        </label>
                    ))}
                </div>
            </div>
            <hr className="rpt-divider" />
            <label className={`rpt-checkbox ${form.confirmed_accurate ? "rpt-checkbox--checked" : ""}`}
                style={errors.confirmed_accurate ? { borderColor: "#ef4444" } : undefined}>
                <input type="checkbox" checked={form.confirmed_accurate}
                    onChange={(e) => set("confirmed_accurate", e.target.checked)} />
                <span className="rpt-checkbox__text">
                    I confirm this report is accurate to the best of my knowledge.
                </span>
            </label>
            {errors.confirmed_accurate && <span className="rpt-error">{errors.confirmed_accurate}</span>}
            {saveError && <div className="rpt-save-error" style={{ marginTop: 12 }}>{saveError}</div>}
        </>
    );

    const renderSlide = () => {
        switch (currentStep) {
            case "intro":          return renderIntro();
            case "contact":        return renderContact();
            case "product":        return renderProduct();
            case "classification": return renderClassification();
            case "health":         return renderHealth();
            case "observations":   return renderObservations();
            case "additional":     return renderAdditional();
            case "notification":   return renderNotification();
            default:               return null;
        }
    };

    const renderConfirmation = () => (
        <div className="rpt-confirm">
            <div className="rpt-confirm__icon">&#10003;</div>
            <h2 className="rpt-confirm__title">Report Submitted Successfully</h2>
            {refNumber && <span className="rpt-confirm__ref">{refNumber}</span>}
            <p className="rpt-confirm__text">
                Thank you for your report. Your information helps identify potential safety issues
                and protect other consumers.
                {(form.contact_preference !== "no" || form.notification_preference !== "no") &&
                    " We will reach out using the contact method you selected."}
            </p>
            <a className="rpt-confirm__physician" href={PHYSICIAN_URL}
                target="_blank" rel="noopener noreferrer">
                🩺 Speak with a Physician
            </a>
            <button className="rpt-confirm__close" onClick={goBack}>
                Back to Compliance Menu
            </button>
        </div>
    );

    // ── Main render ──

    if (pageLoading || draftLoading) {
        return (
            <div className="rpt-page rpt-page--loading">
                <div className="pc-spinner" aria-label="Loading..." />
            </div>
        );
    }

    if (pageError) {
        return (
            <div className="rpt-page rpt-page--loading">
                <div className="rpt-page__error">
                    <p>{pageError}</p>
                    <button className="rpt-nav__back" onClick={goBack}>Back to Compliance Menu</button>
                </div>
            </div>
        );
    }

    const progressPct = isFirst ? 0 : ((stepIndex) / (activeSteps.length - 1)) * 100;
    const hideBottomNav = isFirst && hasDraft;

    return (
        <div className="rpt-page">
            {/* Top bar */}
            <header className="rpt-topbar">
                <button className="rpt-topbar__back" onClick={goBack} aria-label="Back to compliance menu">
                    &#8592; Back
                </button>
                <span className="rpt-topbar__title">
                    {submitted ? "Report Submitted" : isFirst ? "Product Report" : `Step ${stepIndex} of ${activeSteps.length - 1}`}
                </span>
                {saving && <span className="rpt-saving">Saving...</span>}
            </header>

            {/* Progress bar */}
            {!isFirst && !submitted && (
                <div className="rpt-progress">
                    <div className="rpt-progress__track">
                        <div className="rpt-progress__fill" style={{ width: `${progressPct}%` }} />
                    </div>
                </div>
            )}

            {/* Content */}
            <main className="rpt-page__body">
                {submitted ? renderConfirmation() : (
                    <div key={`slide-${animKey}`} className={`rpt-slide--${direction}`}>
                        {renderSlide()}
                    </div>
                )}
            </main>

            {/* Honeypot */}
            <div className="rpt-hp" aria-hidden="true" tabIndex={-1}>
                <label>Leave this empty</label>
                <input type="text" name="website" value={form._hp}
                    onChange={(e) => set("_hp", e.target.value)}
                    tabIndex={-1} autoComplete="off" />
            </div>

            {/* Navigation footer */}
            {!submitted && !hideBottomNav && (
                <footer className="rpt-nav">
                    {!isFirst ? (
                        <button className="rpt-nav__back" onClick={handleBack}>Back</button>
                    ) : <span />}
                    {isLast ? (
                        <button className="rpt-nav__next" onClick={handleSubmit} disabled={saving}>
                            {saving ? "Submitting..." : "Submit Report"}
                        </button>
                    ) : (
                        <button className="rpt-nav__next" onClick={handleNext} disabled={saving}>
                            {isFirst ? "Begin Report" : "Next"}
                        </button>
                    )}
                </footer>
            )}
        </div>
    );
}
